import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { 
  ArrowLeft, FileText, Upload, Download, Eye, CheckCircle, 
  AlertCircle, Calendar, Trash2, X, Plus, Loader2 
} from 'lucide-react';
import { DriverBottomNav } from '../components/DriverBottomNav';
import { documentService } from '../services/documentService';
import { driverService } from '../services/driverService';
import { isExternalDriverUser } from '../utils/userRoles';
import { isCorporateExternalTransportMobileUser } from '../utils/corporateMobileUser';

const FILE_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const resolveFileUrl = (value?: string) => {
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value) || value.startsWith('data:')) return value;
  return `${FILE_BASE_URL}${value.startsWith('/') ? value : `/${value}`}`;
};

interface Document {
  id: string;
  name: string;
  type: string;
  uploadDate: string;
  expiryDate?: string;
  status: 'valid' | 'expiring' | 'expired';
  fileSize: string;
  fileData?: string; // Base64 or URL
  fileName?: string;
  mimeType?: string;
}

const REQUIRED_DOCUMENTS = [
  { key: 'tax-plate', label: 'Vergi levhası', required: true },
  { key: 'license', label: 'Ehliyet', required: true },
  { key: 'tractor-license', label: 'Çekici ruhsat', required: true },
  { key: 'trailer-license', label: 'Dorse ruhsat', required: true },
  { key: 'src', label: 'SRC belgesi', required: true },
  { key: 'vehicle-insurance', label: 'Araç sigortası', required: true },
  { key: 'contract', label: 'Taşıma sözleşmesi', required: true },
  { key: 'carrier-authority', label: 'Taşıyıcı yetki belgesi', required: true },
  { key: 'psychotechnic', label: 'Psikoteknik belgesi', required: true },
];

const CORPORATE_EXTERNAL_TRANSPORT_DOCUMENTS = [
  { key: 'tax-plate', label: 'Vergi levhası', required: true },
  { key: 'carrier-authority', label: 'Taşıyıcı yetki belgesi', required: true },
  { key: 'contract', label: 'Taşıma sözleşmesi', required: true },
];

export function DriverMobileDocuments() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    type: 'other' as string,
    expiryDate: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewDoc, setPreviewDoc] = useState<Document | null>(null);
  const [error, setError] = useState<string | null>(null);
  const activeRequiredDocuments = isCorporateExternalTransportMobileUser(user) ? CORPORATE_EXTERNAL_TRANSPORT_DOCUMENTS : REQUIRED_DOCUMENTS;

  useEffect(() => {
    const initialize = async () => {
      const userData = sessionStorage.getItem('driverMobileUser');
      if (!userData) {
        navigate('/driver-mobile-auth', { replace: true });
        return;
      }
      try {
        setUser(JSON.parse(userData));
        await loadDocuments();
      } catch (error) {
        console.error('Error loading user data:', error);
        navigate('/driver-mobile-auth', { replace: true });
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, [navigate]);

  const loadDocuments = async () => {
    try {
      setError(null);
      const userData = sessionStorage.getItem('driverMobileUser');
      const currentUser = userData ? JSON.parse(userData) : null;
      const response = await documentService.getDocuments({ entityType: 'driver', entityId: String(currentUser?.id || '') });
      const data = response.data || response;
      const currentRequiredDocuments = isCorporateExternalTransportMobileUser(currentUser) ? CORPORATE_EXTERNAL_TRANSPORT_DOCUMENTS : REQUIRED_DOCUMENTS;
      const allowedTypes = isCorporateExternalTransportMobileUser(currentUser) ? new Set(currentRequiredDocuments.map((item) => item.key)) : null;
      setDocuments((Array.isArray(data) ? data : []).filter((doc: any) => !allowedTypes || allowedTypes.has(String(doc.type || doc.document_type || 'other'))).map((doc: any) => ({
        id: String(doc.id || doc.document_id),
        name: doc.name || doc.file_name || 'Belge',
        type: doc.type || doc.document_type || 'other',
        uploadDate: new Date(doc.created_at || Date.now()).toLocaleDateString('tr-TR'),
        expiryDate: doc.expiry_date ? new Date(doc.expiry_date).toLocaleDateString('tr-TR') : undefined,
        status: calculateStatus(doc.expiry_date ? new Date(doc.expiry_date).toLocaleDateString('tr-TR') : undefined),
        fileSize: doc.file_size ? `${(Number(doc.file_size) / (1024 * 1024)).toFixed(2)} MB` : '-',
        fileData: resolveFileUrl(doc.file_url || doc.file_path),
        fileName: doc.file_name,
        mimeType: doc.mime_type,
      })));
    } catch (error) {
      console.error('Error loading documents:', error);
      setError('Belgeler yuklenemedi');
      setDocuments([]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('Dosya boyutu 10MB\'dan küçük olmalıdır');
        return;
      }
      
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        alert('Sadece JPG, PNG veya PDF dosyaları yüklenebilir');
        return;
      }
      
      setSelectedFile(file);
      setUploadForm({
        ...uploadForm,
        name: file.name.split('.')[0], // Remove extension
      });
    }
  };

  const calculateStatus = (expiryDate?: string): 'valid' | 'expiring' | 'expired' => {
    if (!expiryDate) return 'valid';
    
    const expiry = new Date(expiryDate.split('.').reverse().join('-'));
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= 30) return 'expiring';
    return 'valid';
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadForm.name) {
      alert('Lütfen tüm alanları doldurun');
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', selectedFile);
      formDataToSend.append('entityType', 'driver');
      formDataToSend.append('entityId', String(user?.id || ''));
      formDataToSend.append('documentType', uploadForm.type);
      formDataToSend.append('name', uploadForm.name);

      let ocrResult: any = null;
      if (uploadForm.type === 'tax-plate') {
        try {
          const ocrResponse = await documentService.ocrTaxPlate(selectedFile);
          if (ocrResponse.success && ocrResponse.data) {
            ocrResult = ocrResponse.data;
            formDataToSend.append('ocrMetadata', JSON.stringify({
              verified: true,
              taxNumber: ocrResult.taxNumber,
              invoiceTitle: ocrResult.invoiceTitle,
              taxOffice: ocrResult.taxOffice,
              confidence: ocrResult.confidence,
              source: 'ocr-tesseract',
              rawText: ocrResult.rawText?.substring(0, 200),
            }));
          }
        } catch (ocrError) {
          console.warn('OCR failed, using profile data:', ocrError);
        }
      }

      if (uploadForm.expiryDate) formDataToSend.append('expiryDate', uploadForm.expiryDate);

      await documentService.uploadDocument(formDataToSend);
      await loadDocuments();

      try {
        const isExternalDriver = isExternalDriverUser(user);
        const refreshed = await driverService.getDriverByUserId(user?.id);
        const profile = refreshed.data || refreshed;

        const updatedTaxData: any = {};
        if (ocrResult?.invoiceTitle) updatedTaxData.invoice_title = ocrResult.invoiceTitle;
        if (ocrResult?.taxOffice) updatedTaxData.tax_office = ocrResult.taxOffice;
        if (ocrResult?.taxNumber) updatedTaxData.tax_number = ocrResult.taxNumber;

        if (Object.keys(updatedTaxData).length > 0) {
          if (isExternalDriver) {
            await driverService.updateDriver(user?.driverId || user?.id, updatedTaxData);
          } else {
            await driverService.updateDriver(user?.id, updatedTaxData);
          }
        }

        const updatedUser = {
          ...user,
          invoiceTitle: profile?.invoice_title || ocrResult?.invoiceTitle || user?.invoiceTitle || '',
          taxOffice: profile?.tax_office || ocrResult?.taxOffice || user?.taxOffice || '',
          taxNumber: profile?.tax_number || ocrResult?.taxNumber || user?.taxNumber || '',
        };
        setUser(updatedUser);
        sessionStorage.setItem('driverMobileUser', JSON.stringify(updatedUser));
      } catch (syncError) {
        console.error('Failed to sync tax profile:', syncError);
      }

      setShowUploadModal(false);
      setSelectedFile(null);
      setUploadForm({ name: '', type: activeRequiredDocuments[0]?.key || 'other', expiryDate: '' });

      if (uploadForm.type === 'tax-plate' && ocrResult?.confidence) {
        alert(`Belge başarıyla yüklendi!\n\nOCR Güvenilirliği: %${Math.round(ocrResult.confidence)}`);
      } else {
        alert('Belge başarıyla yüklendi!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Belge yüklenirken bir hata oluştu');
    }
  };

  const handleView = (doc: Document) => {
    setPreviewDoc(doc);
  };

  const handleDownload = (doc: Document) => {
    if (doc.fileData) {
      const link = document.createElement('a');
      link.href = doc.fileData;
      link.download = doc.fileName || doc.name;
      link.click();
    } else {
      alert('Bu belge için indirme özelliği mevcut değil');
    }
  };

  const handleDelete = (docId: string) => {
    if (confirm('Bu belgeyi silmek istediğinizden emin misiniz?')) {
      documentService.deleteDocument(docId)
        .then(() => setDocuments(documents.filter(d => d.id !== docId)))
        .catch((error) => console.error('Delete error:', error));
    }
  };

  const openUploadModal = () => {
    setShowUploadModal(true);
  };

  const getTypeIcon = (type: string) => {
    return <FileText className="w-5 h-5" />;
  };

  const getTypeName = (type: string) => {
    switch (type) {
      case 'license':
        return 'Lisans/Belge';
      case 'insurance':
        return 'Sigorta';
      case 'vehicle':
        return 'Araç Belgesi';
      case 'contract':
        return 'Sözleşme';
      case 'tax-plate':
        return 'Vergi Levhası';
      case 'tractor-license':
        return 'Çekici Ruhsat';
      case 'trailer-license':
        return 'Dorse Ruhsat';
      case 'tractor-inspection':
        return 'Çekici Muayene Belgesi';
      case 'trailer-inspection':
        return 'Dorse Muayene Belgesi';
      case 'src':
        return 'SRC Belgesi';
      case 'vehicle-insurance':
        return 'Araç Sigortası';
      case 'carrier-authority':
        return 'Taşıyıcı Yetki Belgesi';
      case 'psychotechnic':
        return 'Psikoteknik';
      case 'other':
        return 'Diğer';
      default:
        return 'Belge';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'valid':
        return 'bg-green-100 text-green-700';
      case 'expiring':
        return 'bg-orange-100 text-orange-700';
      case 'expired':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'valid':
        return <CheckCircle className="w-4 h-4" />;
      case 'expiring':
        return <AlertCircle className="w-4 h-4" />;
      case 'expired':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'valid':
        return 'Geçerli';
      case 'expiring':
        return 'Süresi Dolmak Üzere';
      case 'expired':
        return 'Süresi Dolmuş';
      default:
        return 'Bilinmiyor';
    }
  };

  const stats = {
    total: documents.length,
    valid: documents.filter(d => d.status === 'valid').length,
    expiring: documents.filter(d => d.status === 'expiring').length,
    expired: documents.filter(d => d.status === 'expired').length,
  };

  const profileCompletion = Math.round((activeRequiredDocuments.filter((item) => documents.some((doc) => doc.type === item.key || doc.name.toLowerCase().includes(item.label.toLowerCase()))).length / activeRequiredDocuments.filter((item) => item.required).length) * 100);

  if (!user) return null;

  return (
    <div className="safe-area-page min-h-dvh bg-gray-50 dark:bg-[#0b0b0b]">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white p-4 pb-6 shadow-sm dark:border-slate-800 dark:bg-gradient-to-r dark:from-purple-600 dark:to-purple-700 dark:text-white dark:shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-slate-900 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Geri</span>
          </button>
          <h1 className="text-lg font-bold text-slate-900 dark:text-white">Belgelerim</h1>
          <button 
            onClick={openUploadModal}
            className="rounded-xl bg-slate-100 p-2 text-slate-800 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20"
          >
            <Upload className="w-5 h-5" />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-center dark:border-white/10 dark:bg-white/10">
            <p className="mb-1 text-xs text-slate-600 dark:text-purple-100">Geçerli</p>
            <p className="text-2xl font-bold text-green-700 dark:text-green-300">{stats.valid}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-center dark:border-white/10 dark:bg-white/10">
            <p className="mb-1 text-xs text-slate-600 dark:text-purple-100">Dolmak Üzere</p>
            <p className="text-2xl font-bold text-orange-700 dark:text-orange-300">{stats.expiring}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-center dark:border-white/10 dark:bg-white/10">
            <p className="mb-1 text-xs text-slate-600 dark:text-purple-100">Dolmuş</p>
            <p className="text-2xl font-bold text-red-700 dark:text-red-300">{stats.expired}</p>
          </div>
        </div>
      </div>

      {/* Upload Button */}
      <div className="px-4 pt-4">
        <button 
          onClick={openUploadModal}
          className="w-full bg-purple-600 text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-purple-700 transition-colors"
        >
          <Upload className="w-5 h-5" />
          Yeni Belge Yükle
        </button>
      </div>

      <div className="px-4 pt-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-[#111111]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-gray-900">Profil Doluluğu</h2>
            <span className="text-sm font-semibold text-purple-700">%{profileCompletion}</span>
          </div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden mb-4">
            <div className="h-full bg-gradient-to-r from-purple-500 to-green-500" style={{ width: `${profileCompletion}%` }} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {activeRequiredDocuments.map((item) => {
              const uploaded = documents.find((doc) => doc.type === item.key || doc.name.toLowerCase().includes(item.label.toLowerCase()));
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    setUploadForm({ name: item.label, type: item.key as any, expiryDate: '' });
                    setShowUploadModal(true);
                  }}
                  className="text-left p-3 rounded-xl border border-gray-200 hover:border-purple-400 hover:bg-purple-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-900">{item.label}{item.required ? ' *' : ''}</p>
                      <p className="text-xs text-gray-500 mt-1">{uploaded ? 'Yüklendi' : 'Yükle'}</p>
                    </div>
                    {uploaded ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Upload className="w-5 h-5 text-gray-400" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Documents List */}
      <div className="px-4 pt-4 space-y-3">
        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-center text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">{error}</div>
        )}
        {loading && (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center dark:border-slate-800 dark:bg-[#111111]">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-slate-400">Belgeler yukleniyor</p>
          </div>
        )}
        {!loading && documents.map((doc) => (
          <div
            key={doc.id}
            className={`bg-white rounded-xl shadow-sm overflow-hidden border-l-4 ${
              doc.status === 'valid' ? 'border-green-500' :
              doc.status === 'expiring' ? 'border-orange-500' :
              'border-red-500'
            }`}
          >
            <div className="p-4">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3 flex-1">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    doc.status === 'valid' ? 'bg-green-100 text-green-600' :
                    doc.status === 'expiring' ? 'bg-orange-100 text-orange-600' :
                    'bg-red-100 text-red-600'
                  }`}>
                    {getTypeIcon(doc.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm mb-1">{doc.name}</p>
                    <p className="text-xs text-gray-500">{getTypeName(doc.type)}</p>
                  </div>
                </div>
                <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getStatusColor(doc.status)}`}>
                  {getStatusIcon(doc.status)}
                  <span className="hidden sm:inline">{getStatusText(doc.status)}</span>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between text-gray-600">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Yükleme: {doc.uploadDate}
                  </span>
                  <span className="text-xs">{doc.fileSize}</span>
                </div>
                {doc.expiryDate && (
                  <div className="flex items-center gap-1 text-gray-600">
                    <AlertCircle className="w-4 h-4" />
                    <span>Son Geçerlilik: {doc.expiryDate}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-3 pt-3 border-t">
                <button 
                  onClick={() => handleView(doc)}
                  className="flex-1 py-2 px-3 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                >
                  <Eye className="w-4 h-4" />
                  Görüntüle
                </button>
                <button 
                  onClick={() => handleDownload(doc)}
                  className="flex-1 py-2 px-3 bg-green-50 text-green-600 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors flex items-center justify-center gap-1"
                >
                  <Download className="w-4 h-4" />
                  İndir
                </button>
                <button 
                  onClick={() => handleDelete(doc.id)}
                  className="py-2 px-3 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {documents.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center dark:border-slate-800 dark:bg-[#111111]">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="mb-4 text-gray-500 dark:text-slate-400">Henüz belge yüklenmemiş</p>
            <button 
              onClick={openUploadModal}
              className="bg-purple-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-purple-700 transition-colors"
            >
              İlk Belgeyi Yükle
            </button>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-4">
          <div className="mb-20 max-h-[calc(100vh-6rem)] w-full max-w-md overflow-y-auto rounded-t-3xl bg-white sm:mb-0 sm:max-h-[90vh] sm:rounded-xl dark:bg-[#111111]">
            <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-[#111111]">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">Yeni Belge Yükle</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  setUploadForm({ name: '', type: activeRequiredDocuments[0]?.key || 'other', expiryDate: '' });
                }}
                className="rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-white/5"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 pb-28 sm:pb-4 space-y-4">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Belge Dosyası *
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => {
                    try {
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                        fileInputRef.current.click();
                      }
                    } catch (error) {
                      console.error('File input failed:', error);
                    }
                  }}
                  className="flex w-full flex-col items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 p-6 transition-colors hover:border-purple-500 hover:bg-purple-50 dark:border-slate-700 dark:hover:bg-purple-950/20"
                >
                  {selectedFile ? (
                    <>
                      <FileText className="w-12 h-12 text-purple-600" />
                      <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-gray-400" />
                      <p className="text-sm font-medium text-gray-700">Dosya Seçin</p>
                      <p className="text-xs text-gray-500">PDF, JPG veya PNG (Max 10MB)</p>
                    </>
                  )}
                </button>
              </div>

              {/* Document Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Belge Adı *
                </label>
                <input
                  type="text"
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-transparent focus:ring-2 focus:ring-purple-500 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
                  placeholder="Örn: Sürücü Belgesi"
                />
              </div>

              {/* Document Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Belge Türü *
                </label>
                <select
                  value={uploadForm.type}
                  onChange={(e) => setUploadForm({ ...uploadForm, type: e.target.value as any })}
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-transparent focus:ring-2 focus:ring-purple-500 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
                >
                  {activeRequiredDocuments.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                  {!isCorporateExternalTransportMobileUser(user) ? <option value="other">Diğer</option> : null}
                </select>
              </div>

              {/* Expiry Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Son Geçerlilik Tarihi (Opsiyonel)
                </label>
                <input
                  type="date"
                  value={uploadForm.expiryDate}
                  onChange={(e) => setUploadForm({ ...uploadForm, expiryDate: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Upload Button */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                    setUploadForm({ name: '', type: activeRequiredDocuments[0]?.key || 'other', expiryDate: '' });
                  }}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || !uploadForm.name}
                  className="flex-1 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Yükle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gray-900 px-4 py-3 flex items-center justify-between">
              <h3 className="font-bold text-white">{previewDoc.name}</h3>
              <button
                onClick={() => setPreviewDoc(null)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="flex-1 overflow-auto bg-gray-100 p-4 flex items-center justify-center">
              {previewDoc.fileData ? (
                previewDoc.mimeType?.includes('pdf') ? (
                  <iframe
                    src={previewDoc.fileData}
                    className="w-full h-full min-h-[500px]"
                    title={previewDoc.name}
                  />
                ) : (
                  <img
                    src={previewDoc.fileData}
                    alt={previewDoc.name}
                    className="max-w-full max-h-full object-contain"
                  />
                )
              ) : (
                <div className="text-center">
                  <FileText className="w-24 h-24 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Bu belge için önizleme mevcut değil</p>
                  <button
                    onClick={() => handleDownload(previewDoc)}
                    className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Belgeyi İndir
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <DriverBottomNav />
    </div>
  );
}
